import DeleteOutline from "@mui/icons-material/DeleteOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { Box, ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import { type EdgeTypes, type NodeTypes, type XYPosition } from "@xyflow/react";
import * as React from "react";
import { type ComponentType, type Dispatch, type MouseEvent, type ReactNode, type SetStateAction,useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useBlocker, useNavigate, useParams } from "react-router-dom";

import { VARIABLE_DATA_TYPE_OPTIONS } from "@/constants/rule-variables";
import { clearPageHeader, setPageHeader } from "@/redux/pageHeaderSlice";

import Canvas from "./canvas/Canvas";
import { RULE_CANVAS_GRID_SIZE } from "./canvas/constants";
import LeftPanelResizer, { useLeftPanelWidth } from "./canvas/LeftPanelResizer";
import { createConnectionLine } from "./edges/ConnectionLine";
import DecisionFalseEdge from "./edges/DecisionFalseEdge";
import DecisionTrueEdge from "./edges/DecisionTrueEdge";
// edges
import DefaultEdge from "./edges/DefaultEdge";
import LoopAfterLastEdge from "./edges/LoopAfterLastEdge";
import LoopContinueToStartEdge from "./edges/LoopContinueToStartEdge";
import LoopForEachEdge from "./edges/LoopForEachEdge";
import RuleFooter from "./footer/RuleFooter";
import ActionForm from "./forms/ActionForm";
import AssignmentForm from "./forms/AssignmentForm";
import ClearReactionForm from "./forms/ClearReactionForm";
import DecisionForm from "./forms/DecisionForm";
import EventListenerForm from "./forms/EventListenerForm";
import FilterForm, { type FilterFormState } from "./forms/FilterForm";
import LoopForm from "./forms/LoopForm";
import QueryBuilderForm, { type QueryBuilderFormState } from "./forms/QueryBuilderForm";
import ReactionForm, { type ReactionFormState } from "./forms/ReactionForm";
import SortForm, { type SortFormState } from "./forms/SortForm";
import StopForm from "./forms/StopForm";
import useFormModals, { type FormModalKey } from "./hooks/useFormModals";
import useRuleFlow from "./hooks/useRuleFlow";
import useRuleLifecycle from "./hooks/useRuleLifecycle";
import type { RuleVariable } from "./hooks/useRuleVariables";
import useRuleVariables from "./hooks/useRuleVariables";
import CreateRuleModal, { type RuleGroupOption } from "./modals/CreateRuleModal";
import UnsavedChangesModal from "./modals/UnsavedChangesModal";
import { type AddNodeMenuContextData, AddNodeMenuProvider, useAddNodeMenu } from "./nodes/add-node-menu-context";
import AddNodeMenu from "./nodes/AddNodeMenu";
import { RuleScreenFieldDisplayProvider } from "./nodes/rule-screen-field-display-context";
import ActionNode from "./nodes/ActionNode";
import AssignmentNode from "./nodes/AssignmentNode";
import ClearReactionNode from "./nodes/ClearReactionNode";
import CoAppGradientDefs from "./nodes/CoAppGradientDefs";
import DecisionNode from "./nodes/DecisionNode";
import EventListenerNode from "./nodes/EventListenerNode";
import FilterNode from "./nodes/FilterNode";
import { GhostNode } from "./nodes/GhostNode";
import { type LoopFormConfig,LoopNavigationProvider, useLoopNavigation } from "./nodes/loop/loopContext";
import LoopNode from "./nodes/loop/LoopNode";
import LoopSubCanvas from "./nodes/loop/LoopSubCanvas";
// useLoopNavigation is used in LoopCanvasOverlay below
import { collectAllRuleNodesInTree, getLoopNodeData, updateLoopNodeData } from "./nodes/loop/loopTypes";
import { NodeActionsProvider } from "./nodes/node-actions-context";
import { buildActionNode, buildAssignmentNode, buildClearReactionNode, buildDecisionNode, buildEventListenerNode, buildFilterNode, buildGhostNode, buildLoopNode, buildQueryNode, buildReactionNode, buildSortNode, buildStopNode, collectAllLoopNames, collectAllReactionAssignmentDecisionNames, collectTakenDisplayNames, DEFAULT_NODE_OFFSET_Y, DEFAULT_NODE_POSITION, deleteNodePreservingDecisionEdges, findRuleNodeByIdInTree, getUniqueDefaultName, snapPositionToGrid } from "./nodes/nodeFactory";
// nodes
import QueryBuilderNode from "./nodes/QueryBuilderNode";
import ReactionLocationNode from "./nodes/ReactionLocationNode";
import ReactionNode from "./nodes/ReactionNode";
import SortNode from "./nodes/SortNode";
import StopNode from "./nodes/StopNode";
import RuleBuilderOnboarding from "./RuleBuilderOnboarding";
import CanvasScopedToolbox from "./toolbox/CanvasScopedToolbox";

function optionDataTypeLabel(dataTypeId?: number): string | undefined {
	if (dataTypeId == null) return undefined;
	return VARIABLE_DATA_TYPE_OPTIONS.find((o) => Number(o.id) === Number(dataTypeId))?.label;
}

import messageLevels from "@/constants/message-levels";
import pages from "@/constants/pages";
import useToastAlert from "@/hooks/useToastAlert";

import type { Option } from "./constants";
import type { RuleEdge, RuleEdgeProps, RuleEdgeType } from "./types/edges";
import type { ReactionRuleNode, RuleNode, RuleNodeType } from "./types/nodes";
import {
	getDuplicateNameAmongSameNodeTypeError,
	getDuplicateReactionAssignmentDecisionNameError,
} from "./utils/node-name-uniqueness";
import { collectStopTagNamesFromNodes } from "./utils/stop-tag-utils";

/**
 * Serialize nodes + edges for graph dirty checks (stable order). Clears reaction_location
 * fields that mirror rule metadata (title, screen location label) so details-modal edits
 * do not count as canvas changes.
 */
function serializeFlowForGraphDirtyCheck(nodes: RuleNode[], edges: RuleEdge[]): string {
	const nodePayload = [...nodes]
		.sort((a, b) => a.id.localeCompare(b.id))
		.map((n) => {
			if (n.type === "reaction_location" && (n.data as { type?: string }).type === "reaction_location") {
				const d = n.data as Record<string, unknown>;
				return {
					id: n.id,
					type: n.type,
					position: n.position,
					data: { ...d, name: "", screenLocation: "" },
				};
			}
			return { id: n.id, type: n.type, position: n.position, data: n.data };
		});
	const edgePayload = [...edges]
		.sort((a, b) => a.id.localeCompare(b.id))
		.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle, targetHandle: e.targetHandle, type: e.type }));
	return JSON.stringify({ nodes: nodePayload, edges: edgePayload });
}

/** Stable JSON for toolbox variable dirty checks (simple, formula, DB single/multi — excludes reaction, which tracks the canvas). */
function serializeVariablesForDirtyCheck(vars: {
	databaseSingleRow: RuleVariable[];
	databaseMultiRow: RuleVariable[];
	simple: RuleVariable[];
	formula: RuleVariable[];
}): string {
	const normalize = (arr: RuleVariable[]) =>
		[...arr]
			.sort((a, b) => a.id.localeCompare(b.id))
			.map((v) => ({
				id: v.id,
				name: v.name,
				type: v.type,
				dataTypeId: v.dataTypeId,
				value: v.value,
				variableFormula: v.variableFormula,
				source: v.source,
				sourceNodeId: v.sourceNodeId,
				recordChoice: v.recordChoice,
			}));
	return JSON.stringify({
		databaseSingleRow: normalize(vars.databaseSingleRow),
		databaseMultiRow: normalize(vars.databaseMultiRow),
		simple: normalize(vars.simple),
		formula: normalize(vars.formula),
	});
}

/** Manager Approval Popup reactions anywhere in rule graph (nested loop bodies included). */
function collectManagerApprovalReactionResourceOptions(nodes: RuleNode[]): Option[] {
	return nodes
		.filter((n): n is ReactionRuleNode => n.type === "reaction")
		.filter((n) => n.data.reactionTypeName === "managerapprovalpopup")
		.map((n) => ({
			label: String(n.data.name ?? "Reaction"),
			value: n.id,
			dataType: "String",
		}));
}

// ─── Loop canvas overlay ─────────────────────────────────────────────────────

interface LoopCanvasOverlayProps {
  nodes: RuleNode[];
  setNodes: Dispatch<SetStateAction<RuleNode[]>>;
  formConfig: LoopFormConfig;
  managerApprovalReactionResourceOptions: Option[];
  children: ReactNode;
}

/**
 * Renders the main canvas children, plus overlays the LoopSubCanvas when
 * the user has entered a loop via the navigation context.
 */
const LoopCanvasOverlay = ({ nodes, setNodes, formConfig, managerApprovalReactionResourceOptions, children }: LoopCanvasOverlayProps) => {
	const { loopStack, enterLoop, exitTo } = useLoopNavigation();

	const currentLoopData = useMemo(() => {
		if (loopStack.length === 0) return null;
		return getLoopNodeData(nodes, loopStack);
	}, [nodes, loopStack]);

	const handleSyncToParent = useCallback(
		(
			newLoopNodes: RuleNode[],
			newLoopEdges: RuleEdge[],
			forEachPosition?: { x: number; y: number },
			afterLastPosition?: { x: number; y: number },
			continueNodePosX?: number,
			continueNodePosY?: number,
		) => {
			const continueSentinelPosition =
        continueNodePosX !== undefined && continueNodePosY !== undefined
        	? { x: continueNodePosX, y: continueNodePosY }
        	: undefined;
			setNodes((prev) =>
				updateLoopNodeData(
					prev,
					loopStack,
					newLoopNodes,
					newLoopEdges,
					forEachPosition,
					afterLastPosition,
					continueSentinelPosition,
				),
			);
		},
		[setNodes, loopStack],
	);

	return (
		<Box sx={{ position: "relative", flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
			{children}
			{loopStack.length > 0 && currentLoopData && (
				<LoopSubCanvas
					key={loopStack.map((e) => e.loopNodeId).join("__")}
					loopStack={loopStack}
					initialNodes={currentLoopData.loopNodes}
					initialEdges={currentLoopData.loopEdges}
					loopNodeData={currentLoopData.loopNodeData}
					onSyncToParent={handleSyncToParent}
					onEnterLoop={enterLoop}
					onExitTo={exitTo}
					formConfig={formConfig}
					managerApprovalReactionResourceOptions={managerApprovalReactionResourceOptions}
				/>
			)}
		</Box>
	);
};

// ─────────────────────────────────────────────────────────────────────────────

type RuleNodeComponent = NodeTypes[string];

type SimpleNodeConfig = {
	build: (position: XYPosition, id?: string, displayName?: string) => RuleNode;
	/** Base label used with {@link getUniqueDefaultName} so defaults never collide on the canvas. */
	defaultDisplayName: string;
	formKey?: FormModalKey;
	cleanupGhost?: boolean;
};

const nodeTypes = {
	reaction_location: ReactionLocationNode as RuleNodeComponent,
	reaction: ReactionNode as RuleNodeComponent,
	clear_reaction: ClearReactionNode as RuleNodeComponent,
	ghost: GhostNode as RuleNodeComponent,
	query_builder: QueryBuilderNode as RuleNodeComponent,
	decision: DecisionNode as RuleNodeComponent,
	filter: FilterNode as RuleNodeComponent,
	sort: SortNode as RuleNodeComponent,
	loop: LoopNode as RuleNodeComponent,
	assignment: AssignmentNode as RuleNodeComponent,
	stop: StopNode as RuleNodeComponent,
	event_listener: EventListenerNode as RuleNodeComponent,
	action: ActionNode as RuleNodeComponent,
} satisfies Record<RuleNodeType, RuleNodeComponent>;

const edgeTypes: EdgeTypes & Record<RuleEdgeType, ComponentType<RuleEdgeProps>> = {
	default: DefaultEdge,
	"decision-true": DecisionTrueEdge,
	"decision-false": DecisionFalseEdge,
	"loop-start": LoopForEachEdge,
	"loop-end": LoopAfterLastEdge,
	"loop-for-each-return": DefaultEdge,
	"loop-continue-to-start": LoopContinueToStartEdge,
	// loop-back is only rendered in the sub-canvas but must be registered here since it's part of RuleEdgeType
	"loop-back": DefaultEdge,
};

/** Maps node types that have editable forms to their FormModalKey. */
const CLICKABLE_NODE_FORM_KEYS: Partial<Record<string, FormModalKey>> = {
	decision: "decision",
	loop: "loop",
	sort: "sort",
	filter: "filter",
	query_builder: "query_builder",
	reaction: "reaction",
	clear_reaction: "clearReaction",
	assignment: "assignment",
	event_listener: "eventListener",
	action: "action",
	stop: "stop",
};

/** Config for all non-decision node types — drives the generic branch in handleNodeMenuSelect. */
const SIMPLE_NODE_CONFIGS: Record<string, SimpleNodeConfig> = {
	loop: { build: buildLoopNode, defaultDisplayName: "Loop", formKey: "loop" },
	sort: { build: buildSortNode, defaultDisplayName: "Sort", formKey: "sort", cleanupGhost: true },
	filter: { build: buildFilterNode, defaultDisplayName: "Filter", formKey: "filter", cleanupGhost: true },
	query_builder: { build: buildQueryNode, defaultDisplayName: "Query Builder", formKey: "query_builder", cleanupGhost: true },
	reaction: { build: buildReactionNode, defaultDisplayName: "Reaction", formKey: "reaction", cleanupGhost: true },
	clear_reaction: { build: buildClearReactionNode, defaultDisplayName: "Clear Reaction", formKey: "clearReaction", cleanupGhost: true },
	stop: { build: buildStopNode, defaultDisplayName: "Stop", formKey: "stop", cleanupGhost: true },
	assignment: { build: buildAssignmentNode, defaultDisplayName: "Assignment", formKey: "assignment", cleanupGhost: true },
	event_listener: { build: buildEventListenerNode, defaultDisplayName: "Event Listener", formKey: "eventListener", cleanupGhost: true },
	action: { build: buildActionNode, defaultDisplayName: "Action", formKey: "action", cleanupGhost: true },
};

// Tiny wrapper that consumes AddNodeMenu context so it can hand a paneClick
// handler to ReactFlow. Without this, clicking the canvas (after opening the
// add-node menu via a node's "+") does NOT close the menu — ReactFlow
// occasionally swallows the bubble click before ClickAwayListener sees it.
// Putting an explicit `onPaneClick` on ReactFlow is the reliable hook.
type CanvasWithMenuCloseProps = React.ComponentProps<typeof Canvas>;
const CanvasWithMenuClose = (props: CanvasWithMenuCloseProps) => {
	const { closeMenu, isOpen } = useAddNodeMenu();
	const userOnPaneClick = props.onPaneClick;
	return (
		<Canvas
			{...props}
			onPaneClick={(event) => {
				if (isOpen) closeMenu();
				userOnPaneClick?.(event);
			}}
		/>
	);
};

const RuleBuilder = () => {
	const { ruleId: ruleIdParam } = useParams<{ ruleId: string }>();
	const navigate = useNavigate();
	const { handleToastAlert } = useToastAlert();

	const {
		nodes,
		edges,
		setNodes,
		setEdges,
		onNodesChange,
		onEdgesChange,
		onConnect,
		handleFlowInit,
		handleSaveFlow,
		handleRestoreFlow,
		getAnchoredMenuPosition,
		onReconnect,
		onReconnectStart,
		onReconnectEnd,
		reconnectingEdgeRef,
		reactFlowInstance,
	} = useRuleFlow();

	const ConnectionLine = useMemo(
		() => createConnectionLine(reconnectingEdgeRef),
		[reconnectingEdgeRef],
	);

	// Initial fit-view runs as soon as the ReactFlow instance is available.
	useEffect(() => {
		if (reactFlowInstance) {
			const timer = setTimeout(() => {
				reactFlowInstance.fitView({ duration: 300, padding: 0.2 });
			}, 50);
			return () => clearTimeout(timer);
		}
	}, [reactFlowInstance]);

	const {
		formState,
		openNodeForm,
		closeNodeForm,
		closeFormForNode,
		decisionFormInitialState,
		handleDecisionSave,
		loopFormInitialState,
		handleLoopSave,
		loopActiveNodeId,
		sortFormInitialState,
		handleSortSave,
		sortActiveNodeId,
		filterFormInitialState,
		handleFilterSave,
		filterActiveNodeId,
		queryFormInitialState,
		handleQuerySave,
		queryActiveNodeId,
		decisionActiveNodeId,
		reactionActiveNodeId,
		reactionFormInitialState,
		handleReactionSave,
		patchActiveReactionNodeData,
		assignmentFormInitialState,
		handleAssignmentSave,
		assignmentActiveNodeId,
		clearReactionFormInitialState,
		handleClearReactionSave,
		clearReactionActiveNodeId,
		eventListenerFormInitialState,
		handleEventListenerSave,
		eventListenerActiveNodeId,
		actionFormInitialState,
		handleActionSave,
		actionActiveNodeId,
		stopFormInitialState,
		handleStopSave,
		patchActiveStopNodeData,
		stopActiveNodeId,
	} = useFormModals({
		nodes,
		edges,
		setNodes,
		setEdges,
	});

	const ruleVariables = useRuleVariables();
	const {
		databaseSingleRow,
		databaseMultiRow,
		simple,
		formula,
		reaction,
		allVariables,
		addSimpleVariable,
		updateSimpleVariable,
		removeSimpleVariable,
		addFormulaVariable,
		updateFormulaVariable,
		removeFormulaVariable,
		upsertDatabaseVariable,
		cleanOrphanedDatabaseVariables,
		upsertReactionVariable,
		cleanOrphanedReactionVariables,
		resetVariables,
	} = ruleVariables;

	const entireRuleNodes = useMemo(() => collectAllRuleNodesInTree(nodes), [nodes]);

	const ruleStopTagNames = useMemo(
		() => collectStopTagNamesFromNodes(entireRuleNodes),
		[entireRuleNodes],
	);

	const managerApprovalReactionResourceOptions = useMemo(
		() => collectManagerApprovalReactionResourceOptions(entireRuleNodes),
		[entireRuleNodes],
	);

	const activeDatabaseVariableNodeIds = useMemo(
		() =>
			new Set(
				entireRuleNodes
					.filter((n) => n.type === "query_builder" || n.type === "filter" || n.type === "sort" || n.type === "loop")
					.map((n) => n.id),
			),
		[entireRuleNodes],
	);

	const activeReactionNodeIds = useMemo(
		() => new Set(entireRuleNodes.filter((n) => n.type === "reaction").map((n) => n.id)),
		[entireRuleNodes],
	);

	useEffect(() => {
		cleanOrphanedDatabaseVariables(activeDatabaseVariableNodeIds);
	}, [activeDatabaseVariableNodeIds, cleanOrphanedDatabaseVariables]);

	useEffect(() => {
		cleanOrphanedReactionVariables(activeReactionNodeIds);
	}, [activeReactionNodeIds, cleanOrphanedReactionVariables]);

	const resourceVariableGroups = useMemo(() => {
		if (!simple.length) return [];
		return [{
			label: "Simple Variable",
			options: simple.map((v) => ({
				label: v.name,
				value: v.name,
				dataType: optionDataTypeLabel(v.dataTypeId),
			})),
		}];
	}, [simple]);

	const decisionFormulaVariableOptions = useMemo(
		() => formula.map((v) => ({
			label: v.name,
			value: v.name,
			dataType: optionDataTypeLabel(v.dataTypeId),
		})),
		[formula],
	);

	const decisionSingleRowDatabaseVariableOptions = useMemo(
		() => databaseSingleRow.map((v) => ({
			label: v.name,
			value: v.name,
			dataType: optionDataTypeLabel(v.dataTypeId),
		})),
		[databaseSingleRow],
	);

	const reactionVariableOptions = useMemo(
		() => reaction.map((v) => ({ label: v.name, value: v.sourceNodeId ?? v.id })),
		[reaction],
	);

	const metadataSaveBaselinePendingRef = useRef(false);

	const {
		createRuleModalOpen,
		setCreateRuleModalOpen,
		ruleId,
		ruleName,
		setRuleName,
		ruleDescription,
		ruleTags,
		screenLocation,
		ruleGroups,
		platformId,
		setPlatformId,
		platformOptions,
		groupOptions,
		groupsLoading,
		screenLocationOptions,
		screenLocationsLoading,
		tagsInCurrentOrganization,
		stopTagsInOrganization,
		lastSavedAt,
		isCreatingRule,
		createRuleError,
		setCreateRuleError,
		isLoadingRule,
		ruleMetadataForDirtyCheck,
		isSavingRule,
		handleCreateRule,
		handleSaveRuleMetadata,
		handleRuleSave,
		comparativeOperators,
		assignmentOperators,
		globalConstants,
		formulaOperatorOptions,
		expressionFunctionOptions,
		isFormMetadataLoading,
		reactionTypeOptions,
		ruleFieldOptions,
		advancedTriggerOptions,
		collectionOptions,
	} = useRuleLifecycle({
		ruleId: ruleIdParam,
		nodes,
		edges,
		setNodes,
		setEdges,
		variables: {
			databaseSingleRow,
			databaseMultiRow,
			simple,
			formula,
			reaction,
		},
		resetVariables,
		onRuleMetadataSaveSuccess: () => {
			metadataSaveBaselinePendingRef.current = true;
		},
	});

	const comparativeOperatorOptions = useMemo(
		() => Array.from(new Map(comparativeOperators.map((op) => [op.operator, op])).values())
			.map((op) => ({ label: op.operator, value: op.operator })),
		[comparativeOperators],
	);

	const assignmentOperatorOptions = useMemo(
		() => Array.from(new Map(assignmentOperators.map((op) => [op.operator, op])).values())
			.map((op) => ({ label: op.operator, value: op.operator })),
		[assignmentOperators],
	);

	const resolvedScreenLocationOptions = screenLocationOptions.length
		? screenLocationOptions
		: [];

	const createRuleModalInitialState = useMemo(
		() => ({
			ruleName,
			ruleDescription,
			ruleTags,
			screenLocation,
			platformId,
			groups: (ruleGroups ?? [])
				.map((g: { id?: unknown; name?: unknown }) => {
					if (g == null || g.id == null || g.name == null) return null;
					return { id: String(g.id), name: String(g.name) };
				})
				.filter((item): item is RuleGroupOption => item != null),
		}),
		[ruleName, ruleDescription, ruleTags, screenLocation, platformId, ruleGroups],
	);

	// ── Page header context ───────────────────────────────────────────────────
	// Surfaces the current rule's name + status in the global app bar instead
	// of the generic "Rule Builder" placeholder. Cleared on unmount so other
	// pages render their own header.
	const dispatch = useDispatch();
	useEffect(() => {
		if (isLoadingRule) return;  // wait until the rule has loaded
		const isNewRule = !ruleId;
		const displayTitle = (ruleName && ruleName.trim()) || (isNewRule ? "New Rule" : "Untitled Rule");
		dispatch(setPageHeader({
			title: displayTitle,
			breadcrumbs: [
				{ label: "Rules", href: pages.home },
				{ label: displayTitle }
			],
			statusBadge: isNewRule
				? { label: "Draft", tone: "draft" }
				: null  // active/inactive needs to be threaded from rule data — Phase 2.5
		}));
	}, [dispatch, ruleId, ruleName, isLoadingRule]);

	// One-shot re-fit when the rule finishes loading. Tracks ruleId so we only
	// fire ONCE per rule open, not on every nodes/edges change (which would
	// trip an infinite fitView → render → fitView loop).
	const fittedRuleIdRef = useRef<string | number | null>(null);
	useEffect(() => {
		if (!reactFlowInstance) return;
		if (isLoadingRule) return;
		if (!ruleId) return;
		if (fittedRuleIdRef.current === ruleId) return;
		fittedRuleIdRef.current = ruleId;
		const timer = setTimeout(() => {
			reactFlowInstance.fitView({ duration: 300, padding: 0.25, maxZoom: 0.85, minZoom: 0.4 });
		}, 80);
		return () => clearTimeout(timer);
	}, [reactFlowInstance, ruleId, isLoadingRule]);

	useEffect(() => () => {
		dispatch(clearPageHeader());
	}, [dispatch]);

	const findNodeInRuleTree = useCallback((id: string) => findRuleNodeByIdInTree(nodes, id), [nodes]);
	// All loop names across every canvas depth — used for global uniqueness checks.
	const allLoopNames = useMemo(() => collectAllLoopNames(nodes), [nodes]);
	const allReactionAssignmentDecisionNames = useMemo(
		() => collectAllReactionAssignmentDecisionNames(nodes),
		[nodes],
	);
	const takenReactionAssignmentDecisionNamesForDecision = useMemo((): Set<string> => {
		if (!decisionActiveNodeId) return allReactionAssignmentDecisionNames;
		const active = findRuleNodeByIdInTree(nodes, decisionActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allReactionAssignmentDecisionNames;
		const without = new Set(allReactionAssignmentDecisionNames);
		without.delete(activeName);
		return without;
	}, [allReactionAssignmentDecisionNames, decisionActiveNodeId, nodes]);
	const takenReactionAssignmentDecisionNamesForReaction = useMemo((): Set<string> => {
		if (!reactionActiveNodeId) return allReactionAssignmentDecisionNames;
		const active = findRuleNodeByIdInTree(nodes, reactionActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allReactionAssignmentDecisionNames;
		const without = new Set(allReactionAssignmentDecisionNames);
		without.delete(activeName);
		return without;
	}, [allReactionAssignmentDecisionNames, reactionActiveNodeId, nodes]);
	const takenReactionAssignmentDecisionNamesForAssignment = useMemo((): Set<string> => {
		if (!assignmentActiveNodeId) return allReactionAssignmentDecisionNames;
		const active = findRuleNodeByIdInTree(nodes, assignmentActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allReactionAssignmentDecisionNames;
		const without = new Set(allReactionAssignmentDecisionNames);
		without.delete(activeName);
		return without;
	}, [allReactionAssignmentDecisionNames, assignmentActiveNodeId, nodes]);
	const getDuplicateDecisionNameError = useCallback(
		(name: string) =>
			getDuplicateReactionAssignmentDecisionNameError(
				allReactionAssignmentDecisionNames,
				decisionActiveNodeId,
				findNodeInRuleTree,
				name,
			),
		[allReactionAssignmentDecisionNames, decisionActiveNodeId, findNodeInRuleTree],
	);
	const takenLoopNames = useMemo((): Set<string> => {
		if (!loopActiveNodeId) return allLoopNames;
		const active = nodes.find((n) => n.id === loopActiveNodeId);
		const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
		if (!activeName) return allLoopNames;
		const without = new Set(allLoopNames);
		without.delete(activeName);
		return without;
	}, [allLoopNames, loopActiveNodeId, nodes]);
	const getDuplicateLoopNameError = useCallback(
		(name: string): string | undefined => {
			const trimmed = name.trim().toLowerCase();
			if (!trimmed) return undefined;
			const active = loopActiveNodeId ? nodes.find((n) => n.id === loopActiveNodeId) : undefined;
			const activeName = typeof active?.data?.name === "string" ? active.data.name.trim().toLowerCase() : null;
			if (trimmed === activeName) return undefined;
			if (allLoopNames.has(trimmed)) return "This name is already used by another loop node.";
			return undefined;
		},
		[allLoopNames, loopActiveNodeId, nodes],
	);
	const getDuplicateSortNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "sort", sortActiveNodeId, name),
		[nodes, sortActiveNodeId],
	);
	const getDuplicateFilterNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "filter", filterActiveNodeId, name),
		[nodes, filterActiveNodeId],
	);
	const getDuplicateQueryNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "query_builder", queryActiveNodeId, name),
		[nodes, queryActiveNodeId],
	);
	const getDuplicateReactionNameError = useCallback(
		(name: string) =>
			getDuplicateReactionAssignmentDecisionNameError(
				allReactionAssignmentDecisionNames,
				reactionActiveNodeId,
				findNodeInRuleTree,
				name,
			),
		[allReactionAssignmentDecisionNames, reactionActiveNodeId, findNodeInRuleTree],
	);
	const getDuplicateAssignmentNameError = useCallback(
		(name: string) =>
			getDuplicateReactionAssignmentDecisionNameError(
				allReactionAssignmentDecisionNames,
				assignmentActiveNodeId,
				findNodeInRuleTree,
				name,
			),
		[allReactionAssignmentDecisionNames, assignmentActiveNodeId, findNodeInRuleTree],
	);
	const getDuplicateClearReactionNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "clear_reaction", clearReactionActiveNodeId, name),
		[nodes, clearReactionActiveNodeId],
	);
	const getDuplicateEventListenerNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "event_listener", eventListenerActiveNodeId, name),
		[nodes, eventListenerActiveNodeId],
	);
	const getDuplicateActionNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "action", actionActiveNodeId, name),
		[nodes, actionActiveNodeId],
	);
	const getDuplicateStopNameError = useCallback(
		(name: string) => getDuplicateNameAmongSameNodeTypeError(nodes, "stop", stopActiveNodeId, name),
		[nodes, stopActiveNodeId],
	);

	const mainCanvasMenuExclude = useMemo(() => {
		const exclude: string[] = [];
		const hasEventListener = entireRuleNodes.some((n) => n.type === "event_listener");
		if (hasEventListener) exclude.push("event_listener");
		else exclude.push("action");
		return exclude;
	}, [entireRuleNodes]);

	const handleQuerySaveWithVariable = useCallback((state: QueryBuilderFormState) => {
		handleQuerySave(state);

		if (queryActiveNodeId) {
			upsertDatabaseVariable({
				sourceNodeId: queryActiveNodeId,
				name: state.queryName.trim(),
				source: state.databaseTableId,
				recordChoice: state.recordChoice === "allRecords" ? "all" : "first",
			});
		}
	}, [handleQuerySave, queryActiveNodeId, upsertDatabaseVariable]);

	const handleFilterSaveWithVariable = useCallback((state: FilterFormState) => {
		handleFilterSave(state);

		if (filterActiveNodeId) {
			upsertDatabaseVariable({
				sourceNodeId: filterActiveNodeId,
				name: state.filterName.trim(),
				source: state.databaseTableId ?? "",
				recordChoice: state.recordScope === "allRecords" ? "all" : "first",
			});
		}
	}, [handleFilterSave, filterActiveNodeId, upsertDatabaseVariable]);

	const handleSortSaveWithVariable = useCallback((state: SortFormState) => {
		handleSortSave(state);

		if (sortActiveNodeId) {
			upsertDatabaseVariable({
				sourceNodeId: sortActiveNodeId,
				name: state.sortName.trim(),
				source: state.databaseTableId ?? "",
				recordChoice: "all",
			});
		}
	}, [handleSortSave, sortActiveNodeId, upsertDatabaseVariable]);

	const handleReactionSaveWithVariable = useCallback((state: ReactionFormState) => {
		handleReactionSave(state);

		if (reactionActiveNodeId) {
			upsertReactionVariable({
				sourceNodeId: reactionActiveNodeId,
				name: state.reactionName.trim(),
			});
		}
	}, [handleReactionSave, reactionActiveNodeId, upsertReactionVariable]);

	// Unsaved changes: graph (nodes/edges) and toolbox variables (simple, formula, DB single/multi). Reaction variables mirror nodes.
	const [savedFlowSnapshot, setSavedFlowSnapshot] = useState<string | null>(null);
	const [savedVariablesSnapshot, setSavedVariablesSnapshot] = useState<string | null>(null);
	const lastSnapshotRuleIdRef = useRef<string | number | null>(null);

	const currentFlowSnapshot = useMemo(() => serializeFlowForGraphDirtyCheck(nodes, edges), [nodes, edges]);

	const currentVariablesSnapshot = useMemo(
		() =>
			serializeVariablesForDirtyCheck({
				databaseSingleRow,
				databaseMultiRow,
				simple,
				formula,
			}),
		[databaseSingleRow, databaseMultiRow, simple, formula],
	);

	/** Latest snapshots for async save — closure values can lag if the graph updates during PUT. */
	const currentFlowSnapshotRef = useRef(currentFlowSnapshot);
	currentFlowSnapshotRef.current = currentFlowSnapshot;
	const currentVariablesSnapshotRef = useRef(currentVariablesSnapshot);
	currentVariablesSnapshotRef.current = currentVariablesSnapshot;

	const commitSavedBaseline = useCallback(() => {
		setSavedFlowSnapshot(currentFlowSnapshotRef.current);
		setSavedVariablesSnapshot(currentVariablesSnapshotRef.current);
	}, []);

	const hasUnsavedGraphChanges =
		Boolean(ruleId) &&
		!isLoadingRule &&
		savedFlowSnapshot !== null &&
		savedVariablesSnapshot !== null &&
		(currentFlowSnapshot !== savedFlowSnapshot ||
			currentVariablesSnapshot !== savedVariablesSnapshot);
			
	const blocker = useBlocker(
		() => hasUnsavedGraphChanges,
	);

	const unblock = useCallback(() => {
		if (blocker.state === "blocked") {
			commitSavedBaseline();
			blocker.proceed();
		}
	}, [blocker, commitSavedBaseline]);

	const handleCancelExit = useCallback(() => {
		blocker?.reset?.();
	}, [blocker]);

	// Baseline after create, or after an existing rule finishes loading (avoid capturing empty flow before GET completes).
	useEffect(() => {
		if (ruleId && !createRuleModalOpen && !isLoadingRule && lastSnapshotRuleIdRef.current !== ruleId) {
			setSavedFlowSnapshot(currentFlowSnapshot);
			setSavedVariablesSnapshot(currentVariablesSnapshot);
			lastSnapshotRuleIdRef.current = ruleId;
		}
		if (!ruleId) {
			lastSnapshotRuleIdRef.current = null;
			setSavedFlowSnapshot(null);
			setSavedVariablesSnapshot(null);
		}
	}, [ruleId, createRuleModalOpen, isLoadingRule, currentFlowSnapshot, currentVariablesSnapshot]);

	/** `ruleMetadataForDirtyCheck` is required so this still runs when metadata saves but normalized flow is unchanged. */
	useEffect(() => {
		if (metadataSaveBaselinePendingRef.current) {
			setSavedFlowSnapshot(currentFlowSnapshot);
			setSavedVariablesSnapshot(currentVariablesSnapshot);
			metadataSaveBaselinePendingRef.current = false;
		}
	}, [currentFlowSnapshot, currentVariablesSnapshot, ruleMetadataForDirtyCheck]);

	const handleSaveWithSnapshot = useCallback(async () => {
		const saved = await handleRuleSave();
		if (saved) {
			commitSavedBaseline();
		}
	}, [handleRuleSave, commitSavedBaseline]);

	const handleSaveAndLeave = useCallback(async () => {
		await handleSaveWithSnapshot();
		if (blocker.state === "blocked") {
			blocker.proceed();
		}
	}, [blocker, handleSaveWithSnapshot]);

	// ── Loop sub-canvas form config ──────────────────────────────────────────
	const loopFormConfig = useMemo<LoopFormConfig>(
		() => ({
			platformId,
			allLoopNames,
			allReactionAssignmentDecisionNames,
			comparativeOperators,
			comparativeOperatorOptions,
			assignmentOperatorOptions,
			globalConstantOptions: globalConstants,
			formulaOperatorOptions: formulaOperatorOptions as unknown[],
			expressionFunctionOptions: expressionFunctionOptions as unknown[],
			reactionTypeOptions,
			ruleFieldOptions: ruleFieldOptions as { label: string; value: string; [key: string]: unknown }[],
			advancedTriggerOptions: advancedTriggerOptions as { label: string; value: string; [key: string]: unknown }[],
			collectionOptions: collectionOptions as { label: string; value: string; [key: string]: unknown }[],
			screenLocationOptions: resolvedScreenLocationOptions,
			ruleScreenLocationId: screenLocation?.id,
			resourceVariableGroups,
			formulaVariableOptions: decisionFormulaVariableOptions,
			singleRowDatabaseVariableOptions: decisionSingleRowDatabaseVariableOptions,
			databaseMultiRow,
			databaseSingleRow,
			simple,
			formula,
			reaction,
			allVariables,
			reactionVariableOptions,
			addSimpleVariable,
		}),
		 
		[
			platformId,
			allLoopNames,
			allReactionAssignmentDecisionNames,
			comparativeOperators,
			comparativeOperatorOptions,
			assignmentOperatorOptions,
			globalConstants,
			formulaOperatorOptions,
			expressionFunctionOptions,
			reactionTypeOptions,
			ruleFieldOptions,
			advancedTriggerOptions,
			collectionOptions,
			resolvedScreenLocationOptions,
			screenLocation?.id,
			resourceVariableGroups,
			decisionFormulaVariableOptions,
			decisionSingleRowDatabaseVariableOptions,
			databaseMultiRow,
			databaseSingleRow,
			simple,
			formula,
			reaction,
			allVariables,
			reactionVariableOptions,
			addSimpleVariable,
		],
	);

	const handleDatabaseVariableClick = useCallback((variable: RuleVariable) => {
		if (!variable.sourceNodeId) return;

		const targetNodeId = variable.sourceNodeId;
		setNodes((currentNodes) => currentNodes.map((n) => ({
			...n,
			selected: n.id === targetNodeId,
		})));

		if (reactFlowInstance) {
			reactFlowInstance.fitView({
				nodes: [{ id: targetNodeId }],
				duration: 400,
				padding: 0.5,
				maxZoom: 1.5,
			});
		}
	}, [reactFlowInstance, setNodes]);

	const handleNodeMenuSelect = useCallback((itemKey: string, context: AddNodeMenuContextData) => {
		const anchorPosition = getAnchoredMenuPosition(context.anchorEl);
		let newNodeId: string | null = null;

		const NODE_WIDTH = 250;
		// Stop tile is a fixed 32px square (see StopNode). Half-width is used to
		// center it under its (250px) source so the connector is a straight drop.
		const STOP_HALF_WIDTH = 16;

		// A Stop is terminal — a node may have at most one Stop hanging off it.
		// Block adding a second Stop to the same source so the structure can't end
		// up with two stacked stops (per design: "two stops should not be possible").
		if (itemKey === "stop" && context.sourceNodeId) {
			// Match the specific output branch, not just the node: a Decision's
			// true/false handles share one source node, so each branch must be
			// allowed its own Stop independently.
			const branchAlreadyHasStop = edges.some(
				(e) =>
					e.source === context.sourceNodeId &&
					(e.sourceHandle ?? null) === (context.sourceHandleId ?? null) &&
					nodes.find((n) => n.id === e.target)?.type === "stop",
			);
			if (branchAlreadyHasStop) {
				handleToastAlert(messageLevels.INFO, "This step already ends in a Stop.");
				return;
			}
		}

		const insertTargetId = context.targetNodeId;
		const insertEdgeId = context.edgeId;

		let isGhostTarget = false;
		let isRealInsertion = false;

		// Approximate node bbox for collision detection. Real heights vary per
		// node type but everything's >= 90px tall when rendered, so this is a
		// safe lower bound that'll keep nodes visually clear of each other.
		const NODE_BBOX_W = NODE_WIDTH;
		const NODE_BBOX_H = 100;

		const overlapsAny = (
			candidate: XYPosition,
			others: RuleNode[],
			ignoreId?: string,
		) => {
			for (const n of others) {
				if (ignoreId && n.id === ignoreId) continue;
				if (n.type === "ghost") continue;
				const dx = Math.abs(candidate.x - n.position.x);
				const dy = Math.abs(candidate.y - n.position.y);
				if (dx < NODE_BBOX_W && dy < NODE_BBOX_H) return true;
			}
			return false;
		};

		// Walk `position` downward by DEFAULT_NODE_OFFSET_Y until it doesn't
		// overlap any existing real node. Caps at 50 attempts so a buggy
		// dataset can't lock the UI in a loop.
		const findClearPosition = (
			start: XYPosition,
			others: RuleNode[],
			ignoreId?: string,
		): XYPosition => {
			let p = start;
			for (let i = 0; i < 50; i++) {
				if (!overlapsAny(p, others, ignoreId)) return p;
				p = snapPositionToGrid({ x: p.x, y: p.y + DEFAULT_NODE_OFFSET_Y });
			}
			return p;
		};

		const resolvePositionAndNodes = (currentNodes: RuleNode[]) => {
			const sourceNode = context.sourceNodeId
				? currentNodes.find((n) => n.id === context.sourceNodeId)
				: undefined;

			let position: XYPosition | undefined;
			let filteredNodes = currentNodes;

			if (insertTargetId) {
				const ghostNode = currentNodes.find(
					(n) => n.id === insertTargetId && n.type === "ghost",
				);
				if (ghostNode) {
					isGhostTarget = true;
					position = snapPositionToGrid({
						x: ghostNode.position.x - NODE_WIDTH / 2,
						y: ghostNode.position.y,
					});
					filteredNodes = currentNodes.filter((n) => n.id !== insertTargetId);
				} else {
					isRealInsertion = true;
					const targetNode = currentNodes.find((n) => n.id === insertTargetId);
					if (sourceNode && targetNode) {
						position = snapPositionToGrid({
							x: (sourceNode.position.x + targetNode.position.x) / 2,
							y: (sourceNode.position.y + targetNode.position.y) / 2,
						});
					}
				}
			}

			if (!position && sourceNode) {
				const yPosition = anchorPosition?.y ?? sourceNode.position.y + DEFAULT_NODE_OFFSET_Y;
				position = snapPositionToGrid({
					x: sourceNode.position.x,
					y: yPosition,
				});
			}

			if (!position) {
				position = anchorPosition ?? DEFAULT_NODE_POSITION;
			}

			// Bump the candidate down past any existing nodes that occupy the
			// target slot — keeps freshly-added nodes visible instead of
			// stacked under an existing one. (Skip when inserting between two
			// nodes; the midpoint is intentional.)
			if (!isRealInsertion) {
				position = findClearPosition(position, filteredNodes, sourceNode?.id);
			}

			return { position, filteredNodes };
		};

		const restoreEdgeSnapshot = insertEdgeId
			? edges.find((e) => e.id === insertEdgeId) ?? null
			: null;

		const removeOldEdge = () => {
			if (insertEdgeId) {
				setEdges((currentEdges) =>
					currentEdges.filter((e) => e.id !== insertEdgeId),
				);
			}
		};

		// Decision has unique handling (creates ghost nodes for true/false branches)
		if (itemKey === "decision") {
			let trueGhostId: string | null = null;
			let falseGhostId: string | null = null;

			setNodes((currentNodes) => {
				const { position: nextPosition, filteredNodes } = resolvePositionAndNodes(currentNodes);

				const decisionName = getUniqueDefaultName("Decision", allReactionAssignmentDecisionNames, true);
				const newNode = buildDecisionNode(nextPosition, undefined, decisionName);
				newNodeId = newNode.id;

				const nodeCenterX = nextPosition.x + 125;
				const ghostOffsetX = RULE_CANVAS_GRID_SIZE * 8;
				const ghostOffsetY = DEFAULT_NODE_OFFSET_Y + 125;

				const addedNodes: RuleNode[] = [newNode];

				if (!isRealInsertion) {
					const trueGhost = buildGhostNode({
						x: nodeCenterX - ghostOffsetX,
						y: nextPosition.y + ghostOffsetY,
					});
					trueGhostId = trueGhost.id;
					addedNodes.push(trueGhost);
				}

				const falseGhost = buildGhostNode({
					x: nodeCenterX + ghostOffsetX,
					y: nextPosition.y + ghostOffsetY,
				});
				falseGhostId = falseGhost.id;
				addedNodes.push(falseGhost);

				return [...filteredNodes, ...addedNodes];
			});

			removeOldEdge();

			if (context.sourceNodeId && newNodeId) {
				onConnect({
					source: context.sourceNodeId,
					sourceHandle: context.sourceHandleId ?? null,
					target: newNodeId,
					targetHandle: "in",
				});
			}

			if (newNodeId) {
				if (isRealInsertion && insertTargetId) {
					onConnect({ source: newNodeId, sourceHandle: "true", target: insertTargetId, targetHandle: "in" });
				} else if (trueGhostId) {
					onConnect({ source: newNodeId, sourceHandle: "true", target: trueGhostId, targetHandle: null });
				}
			}
			if (newNodeId && falseGhostId) {
				onConnect({ source: newNodeId, sourceHandle: "false", target: falseGhostId, targetHandle: null });
			}

			if (newNodeId) {
				const relatedNodeIds: string[] = [];
				if (trueGhostId) relatedNodeIds.push(trueGhostId);
				if (falseGhostId) relatedNodeIds.push(falseGhostId);
				openNodeForm("decision", newNodeId, {
					isNew: true,
					relatedNodeIds,
					restoreEdge: restoreEdgeSnapshot,
				});
			}
			return;
		}

		// All other node types
		const config = SIMPLE_NODE_CONFIGS[itemKey];
		if (!config) return;

		setNodes((currentNodes) => {
			const resolved = resolvePositionAndNodes(currentNodes);
			const filteredNodes = resolved.filteredNodes;
			let position = resolved.position;
			// Center a Stop horizontally under its source so the connector is a
			// straight vertical drop. The Stop tile is only ~32px wide vs. the
			// 250px standard node, so sharing the source's left x would leave it
			// left-aligned and the connector would run diagonally. Deliberately NOT
			// grid-snapped — snapping (24px) would nudge it off-center and
			// re-introduce the diagonal. Only applies to a sequential add straight
			// off the node's "out" handle; Decision branch (true/false) stops keep
			// their branch position so they stay under their own side.
			const isSequentialStop = context.sourceHandleId == null || context.sourceHandleId === "out";
			if (itemKey === "stop" && context.sourceNodeId && isSequentialStop) {
				const stopSource = currentNodes.find((n) => n.id === context.sourceNodeId);
				if (stopSource) {
					position = { x: stopSource.position.x + NODE_WIDTH / 2 - STOP_HALF_WIDTH, y: position.y };
				}
			}
			// For loop nodes use the globally-scoped name set so that no two loops
			// at any canvas depth can receive the same default name. Reaction / assignment
			// share a global set with decision so defaults never collide across those types.
			const isLoop = itemKey === "loop";
			const isRas = itemKey === "reaction" || itemKey === "assignment";
			const taken = isLoop ? allLoopNames : isRas ? allReactionAssignmentDecisionNames : collectTakenDisplayNames(filteredNodes);
			const displayName = getUniqueDefaultName(config.defaultDisplayName, taken, isLoop || isRas);
			const firstReactionType = reactionTypeOptions[0];
			const newNode =
				itemKey === "reaction"
					? buildReactionNode(
						position,
						undefined,
						displayName,
						firstReactionType?.reactionTypeApiName
							? {
								reactionTypeId: firstReactionType.value,
								reactionTypeName: firstReactionType.reactionTypeApiName,
								reactionTypeFriendlyName: firstReactionType.label,
							}
							: undefined,
					)
					: config.build(position, undefined, displayName);
			newNodeId = newNode.id;
			return [...filteredNodes, newNode];
		});

		removeOldEdge();

		if (context.sourceNodeId && newNodeId) {
			onConnect({
				source: context.sourceNodeId,
				sourceHandle: context.sourceHandleId ?? null,
				target: newNodeId,
				targetHandle: "in",
			});
		}

		if (isRealInsertion && newNodeId && insertTargetId && itemKey !== "stop") {
			onConnect({
				source: newNodeId,
				sourceHandle: "out",
				target: insertTargetId,
				targetHandle: "in",
			});
		}

		if (config.formKey && newNodeId) {
			openNodeForm(config.formKey, newNodeId, {
				isNew: true,
				restoreEdge: restoreEdgeSnapshot,
			});
		}
	}, [allLoopNames, allReactionAssignmentDecisionNames, edges, nodes, handleToastAlert, getAnchoredMenuPosition, onConnect, openNodeForm, reactionTypeOptions, setNodes, setEdges]);

	const handleRuleClose = useCallback(() => {
		navigate(pages.home);
	}, [navigate]);

	const handleNodeClick = useCallback((_event: MouseEvent, node: RuleNode) => {
		const formKey = CLICKABLE_NODE_FORM_KEYS[node.type];
		if (formKey) {
			openNodeForm(formKey, node.id);
		}
	}, [openNodeForm]);

	/**
	 * Context menu on the canvas: reaction_location offers “Edit Rule Details”; all other node types offer delete.
	 */
	const [contextMenu, setContextMenu] = useState<{
		mouseX: number;
		mouseY: number;
		nodeId: string;
		nodeType: RuleNode["type"];
	} | null>(null);
	/** Closes the menu first; `contextMenu` is cleared in `slotProps.transition.onExited` so content does not swap during the exit animation. */
	const [contextMenuOpen, setContextMenuOpen] = useState(false);

	const handleNodeContextMenu = useCallback((event: MouseEvent, node: RuleNode) => {
		event.preventDefault();
		setContextMenu({
			mouseX: event.clientX,
			mouseY: event.clientY,
			nodeId: node.id,
			nodeType: node.type,
		});
		setContextMenuOpen(true);
	}, []);

	const handleContextMenuClose = useCallback(() => {
		setContextMenuOpen(false);
	}, []);

	const handleContextMenuExited = useCallback(() => {
		setContextMenu(null);
	}, []);

	const handleContextMenuEditRuleDetails = useCallback(() => {
		setCreateRuleModalOpen(true);
		setContextMenuOpen(false);
	}, [setCreateRuleModalOpen]);

	const handleContextMenuDelete = useCallback(() => {
		if (!contextMenu) return;
		const { nodeId } = contextMenu;
		const { nodes: updatedNodes, edges: updatedEdges } = deleteNodePreservingDecisionEdges(nodeId, nodes, edges);
		setNodes(updatedNodes);
		setEdges(updatedEdges);
		setContextMenuOpen(false);
	}, [contextMenu, nodes, edges, setNodes, setEdges]);

	const handleInlineNodeDelete = useCallback((nodeId: string) => {
		const { nodes: updatedNodes, edges: updatedEdges } = deleteNodePreservingDecisionEdges(nodeId, nodes, edges);
		setNodes(updatedNodes);
		setEdges(updatedEdges);
		// Close + clear the left-panel form if it belonged to the deleted node.
		closeFormForNode(nodeId);
	}, [nodes, edges, setNodes, setEdges, closeFormForNode]);

	const nodeActionsValue = useMemo(
		() => ({ onDeleteNode: handleInlineNodeDelete }),
		[handleInlineNodeDelete]
	);

	// User-resizable left pane width (persisted to localStorage).
	const { width: leftPaneWidth, setWidth: setLeftPaneWidth } = useLeftPanelWidth();

	// True when any node-config form is open — when so, the form panel
	// occupies the left slot in place of the toolbox.
	const isAnyFormOpen = Boolean(
		formState.decision ||
		formState.loop ||
		formState.sort ||
		formState.filter ||
		formState.query_builder ||
		formState.reaction ||
		formState.assignment ||
		formState.clearReaction ||
		formState.eventListener ||
		formState.action ||
		formState.stop
	);

	return (
		<>
			<CoAppGradientDefs />
			{/* Hold the first-run tour until the "Create Your Rule" modal is done,
			    so it appears only after the user completes that pop-up. */}
			<RuleBuilderOnboarding ready={!createRuleModalOpen} />
			<CreateRuleModal
				open={createRuleModalOpen}
				onClose={() => {
					setCreateRuleModalOpen(false);
					if (!ruleId) {
						navigate(pages.home);
					}
				}}
				onSave={ruleId ? handleSaveRuleMetadata : handleCreateRule}
				isSaving={ruleId ? isSavingRule : isCreatingRule}
				initialState={createRuleModalInitialState}
				editedId={ruleId ? ruleId : null}
				lockScreenLocation={Boolean(ruleId)}
				dialogTitle={ruleId ? "Edit rule details" : "Create Your Rule"}
				submitButtonLabel={ruleId ? "Save" : "Create Rule"}
				screenLocationOptions={resolvedScreenLocationOptions}
				platformOptions={platformOptions}
				groupOptions={groupOptions}
				groupsLoading={groupsLoading}
				setPlatformId={setPlatformId}
				tagsInCurrentOrganization={tagsInCurrentOrganization}
				createRuleError={createRuleError}
				setCreateRuleError={setCreateRuleError}
				screenLocationsLoading={screenLocationsLoading}
			/>
			<Box
				sx={{
					width: "100%",
					height: "100%",
					minHeight: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column"
				}}
			>
				<Box sx={{
					display: "flex",
					flexDirection: "row",
					flexGrow: 1,
					minHeight: 0,
					overflow: "hidden"
				}}>
					<LoopNavigationProvider formConfig={loopFormConfig}>
						<RuleScreenFieldDisplayProvider
							ruleFieldOptions={ruleFieldOptions}
							ruleLocationName={screenLocation?.name}
						>
						{/* Left slot: form panel (when any open) OR the toolbox.
						    Fixed-width container the user can resize via the
						    handle that sits between this and the canvas. */}
						<Box
							data-onboarding="elements"
							sx={{
								flex: `0 0 ${leftPaneWidth}px`,
								width: leftPaneWidth,
								minWidth: 0,
								height: "100%",
								display: "flex",
								flexDirection: "column",
								overflow: "hidden",
							}}
						>
							{isAnyFormOpen ? (
								<>
									<DecisionForm
										key={`DecisionForm-${decisionActiveNodeId ?? "none"}`}
										open={formState.decision}
										onClose={() => closeNodeForm("decision")}
										onSave={handleDecisionSave}
										initialState={decisionFormInitialState}
										platformId={platformId}
										operatorOptions={comparativeOperatorOptions}
										resourceVariableGroups={resourceVariableGroups}
										formulaVariableOptions={decisionFormulaVariableOptions}
										singleRowDatabaseVariableOptions={decisionSingleRowDatabaseVariableOptions}
										resourceScreenFields={ruleFieldOptions}
										resourceAdvancedTriggers={advancedTriggerOptions}
										resourceCollections={collectionOptions}
										globalConstantOptions={globalConstants}
										suppressComparativeOperatorsAutoFetch
										getDuplicateNameError={getDuplicateDecisionNameError}
										takenReactionAssignmentDecisionNames={takenReactionAssignmentDecisionNamesForDecision}
										ruleSimpleVariables={simple}
										ruleFormulaVariables={formula}
										ruleDatabaseSingleRowVariables={databaseSingleRow}
										ruleDatabaseMultiRowVariables={databaseMultiRow}
										managerApprovalReactionResourceOptions={managerApprovalReactionResourceOptions}
										screenLocationOptions={resolvedScreenLocationOptions}
										ruleScreenLocationId={screenLocation?.id}
									/>
									<LoopForm
										key={`LoopForm-${loopActiveNodeId ?? "none"}`}
										open={formState.loop}
										onClose={() => closeNodeForm("loop")}
										onSave={handleLoopSave}
										initialState={loopFormInitialState}
										platformId={platformId}
										getDuplicateNameError={getDuplicateLoopNameError}
										takenLoopNames={takenLoopNames}
										ruleDatabaseMultiRowVariables={databaseMultiRow}
										ruleScreenLocationId={screenLocation?.id}
										collectionOptions={collectionOptions}
									/>
									<SortForm
										key={`SortForm-${sortActiveNodeId ?? "none"}`}
										open={formState.sort}
										onClose={() => closeNodeForm("sort")}
										onSave={handleSortSaveWithVariable}
										initialState={sortFormInitialState}
										platformId={platformId}
										getDuplicateNameError={getDuplicateSortNameError}
										ruleDatabaseMultiRowVariables={databaseMultiRow}
									/>
									<FilterForm
										key={`FilterForm-${filterActiveNodeId ?? "none"}`}
										open={formState.filter}
										onClose={() => closeNodeForm("filter")}
										onSave={handleFilterSaveWithVariable}
										initialState={filterFormInitialState}
										platformId={platformId}
										comparativeOperators={comparativeOperators}
										resourceVariableGroups={resourceVariableGroups}
										formulaVariableOptions={decisionFormulaVariableOptions}
										singleRowDatabaseVariableOptions={decisionSingleRowDatabaseVariableOptions}
										resourceScreenFields={ruleFieldOptions}
										globalConstantOptions={globalConstants}
										suppressComparativeOperatorsAutoFetch
										getDuplicateNameError={getDuplicateFilterNameError}
										ruleSimpleVariables={simple}
										ruleFormulaVariables={formula}
										ruleDatabaseSingleRowVariables={databaseSingleRow}
										ruleDatabaseMultiRowVariables={databaseMultiRow}
									/>
									<QueryBuilderForm
										key={`QueryBuilderForm-${queryActiveNodeId ?? "none"}`}
										open={formState.query_builder}
										onClose={() => closeNodeForm("query_builder")}
										onSave={handleQuerySaveWithVariable}
										initialState={queryFormInitialState}
										platformId={platformId}
										operatorOptions={comparativeOperatorOptions}
										suppressComparativeOperatorsAutoFetch
										getDuplicateNameError={getDuplicateQueryNameError}
									/>
									<ReactionForm
										key={`ReactionForm-${reactionActiveNodeId ?? "none"}`}
										open={formState.reaction}
										onClose={() => closeNodeForm("reaction")}
										onSave={handleReactionSaveWithVariable}
										initialState={reactionFormInitialState}
										reactionTypeOptions={reactionTypeOptions}
										patientFieldOptions={ruleFieldOptions}
										platformId={platformId}
										resourceVariableGroups={resourceVariableGroups}
										formulaVariableOptions={decisionFormulaVariableOptions}
										singleRowDatabaseVariableOptions={decisionSingleRowDatabaseVariableOptions}
										resourceScreenFields={ruleFieldOptions}
										resourceAdvancedTriggers={advancedTriggerOptions}
										screenLocationOptions={resolvedScreenLocationOptions}
										ruleScreenLocationId={screenLocation?.id}
										getDuplicateNameError={getDuplicateReactionNameError}
										takenReactionAssignmentDecisionNames={takenReactionAssignmentDecisionNamesForReaction}
									/>
									<AssignmentForm
										key={`AssignmentForm-${assignmentActiveNodeId ?? "none"}`}
										open={formState.assignment}
										onClose={() => closeNodeForm("assignment")}
										onSave={handleAssignmentSave}
										initialState={assignmentFormInitialState}
										platformId={platformId}
										simpleVariables={simple}
										existingVariables={allVariables}
										onAddSimpleVariable={addSimpleVariable}
										operatorOptions={assignmentOperatorOptions}
										suppressAssignmentOperatorsAutoFetch
										getDuplicateNameError={getDuplicateAssignmentNameError}
										takenReactionAssignmentDecisionNames={takenReactionAssignmentDecisionNamesForAssignment}
										resourceVariableGroups={resourceVariableGroups}
										formulaVariableOptions={decisionFormulaVariableOptions}
										resourceScreenFields={ruleFieldOptions}
										globalConstantOptions={globalConstants}
										ruleSimpleVariables={simple}
										ruleFormulaVariables={formula}
										ruleDatabaseSingleRowVariables={databaseSingleRow}
										ruleDatabaseMultiRowVariables={databaseMultiRow}
									/>
									<ClearReactionForm
										key={`ClearReactionForm-${clearReactionActiveNodeId ?? "none"}`}
										open={formState.clearReaction}
										onClose={() => closeNodeForm("clearReaction")}
										onSave={handleClearReactionSave}
										initialState={clearReactionFormInitialState}
										reactionOptions={reactionVariableOptions}
										getDuplicateNameError={getDuplicateClearReactionNameError}
									/>
									<EventListenerForm
										open={formState.eventListener}
										onClose={() => closeNodeForm("eventListener")}
										onSave={handleEventListenerSave}
										initialState={eventListenerFormInitialState}
										screenLocationId={screenLocation?.id}
										getDuplicateNameError={getDuplicateEventListenerNameError}
									/>
									<ActionForm
										open={formState.action}
										onClose={() => closeNodeForm("action")}
										onSave={handleActionSave}
										initialState={actionFormInitialState}
										getDuplicateNameError={getDuplicateActionNameError}
									/>
									<StopForm
										open={formState.stop}
										onClose={() => closeNodeForm("stop")}
										onSave={handleStopSave}
										onDraftPatch={patchActiveStopNodeData}
										activeNodeId={stopActiveNodeId}
										initialState={stopFormInitialState}
										getDuplicateNameError={getDuplicateStopNameError}
										stopTagsInOrganization={stopTagsInOrganization}
										ruleStopTagNames={ruleStopTagNames}
									/>
								</>
							) : (
								<CanvasScopedToolbox
									nodes={nodes}
									databaseSingleRowVariables={databaseSingleRow}
									databaseMultiRowVariables={databaseMultiRow}
									simpleVariables={simple}
									formulaVariables={formula}
									reactionVariables={reaction}
									fullVariableUniverse={allVariables}
									onAddSimpleVariable={addSimpleVariable}
									onUpdateSimpleVariable={updateSimpleVariable}
									onRemoveSimpleVariable={removeSimpleVariable}
									onAddFormulaVariable={addFormulaVariable}
									onUpdateFormulaVariable={updateFormulaVariable}
									onRemoveFormulaVariable={removeFormulaVariable}
									onDatabaseVariableClick={handleDatabaseVariableClick}
									onReactionVariableClick={handleDatabaseVariableClick}
									formulaOperatorOptions={formulaOperatorOptions}
									expressionFunctionOptions={expressionFunctionOptions}
									isFormMetadataLoading={isFormMetadataLoading}
									platformId={platformId}
									resourceVariableGroups={resourceVariableGroups}
									formulaVariableOptions={decisionFormulaVariableOptions}
									singleRowDatabaseVariableOptions={decisionSingleRowDatabaseVariableOptions}
									resourceScreenFields={ruleFieldOptions}
									resourceAdvancedTriggers={advancedTriggerOptions}
									screenLocationOptions={resolvedScreenLocationOptions}
									ruleScreenLocationId={screenLocation?.id}
									globalConstantOptions={globalConstants}
								/>
							)}
						</Box>
						<LeftPanelResizer width={leftPaneWidth} onWidthChange={setLeftPaneWidth} />
						<LoopCanvasOverlay
							nodes={nodes}
							setNodes={setNodes}
							formConfig={loopFormConfig}
							managerApprovalReactionResourceOptions={managerApprovalReactionResourceOptions}
						>
							<AddNodeMenuProvider onItemSelect={handleNodeMenuSelect}>
								<NodeActionsProvider value={nodeActionsValue}>
									<Box data-onboarding="canvas" sx={{ position: "relative", flex: 1, minWidth: 0 }}>
										<CanvasWithMenuClose
											nodeTypes={nodeTypes as NodeTypes}
											edgeTypes={edgeTypes}
											nodes={nodes}
											edges={edges}
											onNodesChange={onNodesChange}
											onEdgesChange={onEdgesChange}
											onConnect={onConnect}
											onInit={handleFlowInit}
											onNodeClick={handleNodeClick}
											onNodeContextMenu={handleNodeContextMenu}
											onReconnect={onReconnect}
											onReconnectStart={onReconnectStart}
											onReconnectEnd={onReconnectEnd}
											connectionLineComponent={ConnectionLine}
											fitView
											nodesDraggable={true}
											snapToGrid
											snapGrid={[RULE_CANVAS_GRID_SIZE, RULE_CANVAS_GRID_SIZE]}
											gridSize={RULE_CANVAS_GRID_SIZE}
										/>
										<AddNodeMenu excludeTypes={mainCanvasMenuExclude} />
									</Box>
								</NodeActionsProvider>
							</AddNodeMenuProvider>
						</LoopCanvasOverlay>
						</RuleScreenFieldDisplayProvider>
					</LoopNavigationProvider>
				</Box>
				{ruleId ?
					<RuleFooter
						ruleName={ruleName}
						lastSavedAt={lastSavedAt}
						hasUnsavedGraphChanges={hasUnsavedGraphChanges}
						onSave={handleSaveWithSnapshot}
						onClose={handleRuleClose}
						onEditRule={() => setCreateRuleModalOpen(true)}
						isSaving={isSavingRule}
					/>
					: <></>
				}

			</Box>
			{/* Node context menu */}
			<Menu
				open={contextMenuOpen}
				onClose={handleContextMenuClose}
				anchorReference="anchorPosition"
				anchorPosition={
					contextMenu != null
						? { top: contextMenu.mouseY, left: contextMenu.mouseX }
						: undefined
				}
				slotProps={{
					list: { dense: true },
					transition: { onExited: handleContextMenuExited },
				}}
			>
				{contextMenu?.nodeType === "reaction_location" ? (
					<MenuItem onClick={handleContextMenuEditRuleDetails}>
						<ListItemIcon>
							<EditOutlined fontSize="small" />
						</ListItemIcon>
						<ListItemText primary="Edit Rule Details" />
					</MenuItem>
				) : (
					<MenuItem onClick={handleContextMenuDelete}>
						<ListItemIcon>
							<DeleteOutline fontSize="small" color="error" />
						</ListItemIcon>
						<ListItemText primary="Delete node" />
					</MenuItem>
				)}
			</Menu>
			<UnsavedChangesModal
				open={blocker.state === "blocked"}
				onClose={handleCancelExit}
				onExitWithoutSaving={unblock}
				onSaveAndContinue={handleSaveAndLeave}
				isSaving={isSavingRule}
			/>
		</>
	);
};

export default RuleBuilder;
